/**
 * Post-tool structured-event renderer.
 *
 * 2026-05-10 (Miles directive, Slack C0AMX0ACS9K/1778451390.755569):
 *   "this shouldn't be a hardcoded response (never should be). we shouldn't
 *    go down all the edge cases to avoid this issue anywhere in the account
 *    specific Prest0n ever"
 *
 * Tools emit structured events via stream.finalize({ payload }) or
 * helpers.addSystemMessage({ payload }). This module composes the
 * user-visible reply via a single small LLM call. The renderer:
 *   - decides WHETHER to display the data at all (e.g. an environment
 *     survey may not want a 9-role table dump even if RBAC fired);
 *   - decides HOW MUCH of it to show;
 *   - composes the voice / language / format from the persona snippet
 *     and the user's recent conversation messages.
 *
 * Implementation notes (2026-08-20 decision doctrine — Miles orchestrator-
 * authorship directive: special messages ride the PRIMARY orchestrator's
 * plane, never a code-pinned side model):
 *   - Routing: the SSOT decision route `structured-event-narration`
 *     (tier-4-general-default — the default orchestrator plane) via the
 *     server-resolved decision transport (requestDecision). The client
 *     sends the route label ONLY; the server resolves model/ladder/effort
 *     from the registry, so tier swaps reach every client with no deploy.
 *     The receipt then shows the standard-Prest0n model and the ACTUAL
 *     served effort — the old hardcoded gpt-5.6-sol ladder ("prism quartz ·
 *     helm" receipts) is dead on the primary path.
 *   - The one-release deploy-skew fallback (a hardcoded runOpenRouterStream
 *     ladder for old function builds without decision mode) was deleted on
 *     schedule (D14 slice 0, 2026-08-25) — this file now carries NO model
 *     ids and NO side-band egress path.
 *   - Falls back to a deterministic event-summary rendering ONLY when the
 *     decision transport fails (`failOpen: event-summary-text` in the
 *     contract). This fallback is intentionally ugly so a regression is
 *     obvious.
 *   - The renderer NEVER short-circuits to a hardcoded user-facing
 *     string. Hardcoded strings are forbidden by
 *     scripts/forbid-hardcoded-user-strings.py.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { requestDecision } from '../decisionClient';
import { getLastNMessages } from '../../components/GlobalChatSystem/utils';
import { GLOBAL_CHAT_HISTORY_KEY } from '../../components/GlobalChatSystem/types';
import type { TokenUsageMetadata } from '../../components/GlobalChatSystem/types';
import type { ToolStructuredEventBase } from './structuredEvents';

// URL resolution — mirrors inferThreadLabel.ts / legalDocCreator.ts so we
// don't introduce drift.
function resolveAnthropicProxyUrl(): string {
  const override = (process.env.REACT_APP_ANTHROPIC_PROXY_URL || '').trim();
  if (override) return override;
  const projectId =
    process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/anthropicProxy`;
}

// ============================================================================
// Types
// ============================================================================

export interface RenderStructuredEventOptions {
  /** The structured event to render. */
  event: ToolStructuredEventBase;

  /**
   * Optional override for the conversation context (defaults to last 6
   * messages from GLOBAL_CHAT_HISTORY_KEY). Pass an explicit array when
   * the renderer is invoked outside the normal chat context (e.g. tests).
   */
  conversationContext?: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>;

  /**
   * Optional persona snippet (Prest0n persona, tenant tone, etc.). When
   * omitted, a default Prest0n persona snippet is used.
   */
  personaSnippet?: string;

  /**
   * Optional user language hint (e.g. "Spanish", "English (US)"). Falls
   * back to "the user's language" — the LLM will infer from history.
   */
  userLanguage?: string;

  /** Optional abort signal. */
  signal?: AbortSignal;

  /** Streaming chunk callback. */
  onChunk?: (chunk: string, accumulated: string) => void;

  /** Storage key for conversation history (defaults to GLOBAL_CHAT_HISTORY_KEY). */
  historyStorageKey?: string;

  /** Optional organization/workspace context line(s) merged under the
   *  persona (e.g. the firm name) — enrichment, fail-open. */
  orgSnippet?: string;

  /** Optional account id threaded to the decision transport for spend
   *  attribution. */
  accountId?: string;
}

export interface RenderStructuredEventResult {
  /** Plain-text rendered reply, in the user's language and persona voice. */
  text: string;

  /**
   * Optional HTML rendition. The renderer keeps this empty by default —
   * downstream code can wrap `text` in markdown-to-HTML if needed.
   */
  html?: string;

  /** Token usage metadata for the rendering call (for transaction logging). */
  tokenUsage?: TokenUsageMetadata;

  /** Whether the LLM call completed successfully. */
  success: boolean;

  /** When success=false, the error message (rendered as a fallback reply). */
  errorMessage?: string;
}

// ============================================================================
// Default persona snippet
// ============================================================================

const DEFAULT_PERSONA_SNIPPET = `# PERSONA
You are "Prest0n", a legal AI assistant. Your replies are warm, direct, and
professional — never robotic. You speak AS Prest0n and NEVER mention internal
system names ("Maestro", "orchestrator", "Apex-4.7", the codename of the
model you are running on, etc.).
NEVER open a reply with a self-introduction ("I'm Prest0n", "This is
Prest0n") — your name is already displayed on every message, and repeating it
reads as a malfunction (owner directive, 2026-08-12; the old wording of this
snippet actually CAUSED that bug by instructing "always introduce yourself").
Introduce yourself only if the user explicitly asks who or what you are.
`;

// ============================================================================
// Prompt builder
// ============================================================================

/**
 * FORMATTING DOCTRINE chat floor (owner directive 2026-08-19) — the compact
 * chat-message rules every prose-producing surface shares. The full doctrine
 * lives server-side in promptBuilder.buildFormattingDoctrineSection; a
 * cross-tree parity test pins these anchor phrases so the two copies never
 * drift (the shared-package build is the documented follow-up).
 */
const CHAT_FORMAT_FLOOR = [
  '- Lead with the answer or outcome in plain prose; supporting detail after.',
  '- Short paragraphs over walls of text; a blank line between distinct ideas.',
  '- Bullets or numbered lists ONLY for real enumerations — never to disguise prose.',
  '- Links as markdown [meaningful label](url).',
  '- Bold sparingly for the few load-bearing terms. Emphasis everywhere is emphasis nowhere.',
].join('\n');

function buildSystemPrompt(personaSnippet: string, userLanguage: string | undefined): string {
  return `${personaSnippet}

# YOUR JOB
A tool just finished executing and returned a STRUCTURED EVENT. Compose the
user-visible chat reply that incorporates the result of this event. The
event's "data" field contains ALL the underlying information — you decide
which subset to surface, how much detail to show, and how to phrase it.

# RULES
- Reply in ${userLanguage || "the user's language (infer from the conversation history)"}.
- Match the user's tone and verbosity. Short questions get short answers.
- Do NOT dump the entire data structure as a table unless the user explicitly
  asked for one. Summarise when appropriate. Reference the data fields by
  meaningful names ("you have 9 roles configured, 2 of them system-defined")
  rather than reciting field-by-field.
- If the event's "severity" is "error", compose a recoverable error message
  and suggest a next step. Do NOT expose internal stack traces.
- If the user's most recent message was a broader question (e.g. an
  environment survey, a capabilities check) and the structured event is
  only one slice of the answer, integrate the event into the broader
  reply — do NOT let the event displace the broader answer.
- Output PLAIN TEXT or simple markdown. No HTML. No JSON. This surface never
  delivers files or code, so no code fences here.
${CHAT_FORMAT_FLOOR}
- Be concise. Prefer 1-3 short paragraphs over a long list.

# OUTPUT
Just the user-visible reply. No preamble, no "Here is the rendered event",
no JSON wrapper.`;
}

// Context window (2026-08-20): widened from 6 to 20 messages — the narration
// must read like it knows the thread, per the orchestrator-authorship
// directive. Per-message char cap stays; the total is additionally bounded.
const CONTEXT_WINDOW_MESSAGES = 20;
const CONTEXT_TOTAL_CHAR_CAP = 24_000;

function buildUserPrompt(
  event: ToolStructuredEventBase,
  conversationContext: Array<{ role: string; content: string }>,
): string {
  const windowed = conversationContext.slice(-CONTEXT_WINDOW_MESSAGES);
  const lines: string[] = [];
  let total = 0;
  // Newest-first fill against the total cap, then restore order — the most
  // recent turns always survive.
  for (let i = windowed.length - 1; i >= 0; i -= 1) {
    const m = windowed[i];
    const line = `${m.role.toUpperCase()}: ${truncate(m.content, 1500)}`;
    if (total + line.length > CONTEXT_TOTAL_CHAR_CAP && lines.length > 0) break;
    lines.unshift(line);
    total += line.length;
  }
  const recentLines = lines.join('\n\n');

  const eventJson = safeJsonStringify(event);

  return `# RECENT CONVERSATION (last ${lines.length} messages)
${recentLines || '(no prior messages)'}

# STRUCTURED EVENT FROM TOOL
${eventJson}

# YOUR REPLY (composed in the user's language and persona voice):`;
}

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    try {
      return String(value);
    } catch {
      return '[unrenderable]';
    }
  }
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}

// ============================================================================
// Default conversation-context loader
// ============================================================================

function loadDefaultConversationContext(
  storageKey: string = GLOBAL_CHAT_HISTORY_KEY,
): Array<{ role: 'user' | 'assistant' | 'system'; content: string }> {
  try {
    const messages = getLastNMessages(storageKey, CONTEXT_WINDOW_MESSAGES);
    return messages.map((m) => ({
      role: m.isUser ? ('user' as const) : ('assistant' as const),
      content: m.text || '',
    }));
  } catch (err) {
    console.warn('[renderStructuredEvent] failed to load conversation context:', err);
    return [];
  }
}

// ============================================================================
// Public renderer
// ============================================================================

/**
 * Render a tool structured event into a user-visible reply via a single
 * fast-model LLM call. See module docstring for design rationale.
 */
export async function renderStructuredEvent(
  options: RenderStructuredEventOptions,
): Promise<RenderStructuredEventResult> {
  const {
    event,
    personaSnippet,
    userLanguage,
    signal,
    onChunk,
    historyStorageKey,
    orgSnippet,
    accountId,
  } = options;

  const conversationContext =
    options.conversationContext ?? loadDefaultConversationContext(historyStorageKey);

  const basePersona = personaSnippet || DEFAULT_PERSONA_SNIPPET;
  const persona = orgSnippet ? `${basePersona}\n\n# ORGANIZATION CONTEXT\n${orgSnippet}` : basePersona;
  const systemPrompt = buildSystemPrompt(persona, userLanguage);
  const userPrompt = buildUserPrompt(event, conversationContext);

  // ── Primary: the SSOT decision transport (2026-08-20 doctrine) ──────────
  // route: 'structured-event-narration' → tier-4-general-default. The server
  // resolves model/ladder/effort; the client never touches a model id here.
  // JSON-once (no streaming): the "composing reply" widget covers the wait,
  // and the single onChunk delivers the whole text — both messaging call
  // sites accept that shape.
  let lastErr: any = null;
  try {
    const startedAt = Date.now();
    const decided = await requestDecision({
      route: 'structured-event-narration',
      system: systemPrompt,
      messages: [{ role: 'user', content: userPrompt }],
      maxTokens: 1024,
      proxyUrl: resolveAnthropicProxyUrl(),
      ...(accountId ? { accountId } : {}),
      signal,
    });
    const text = (decided.text || '').trim();
    if (text) {
      if (onChunk) onChunk(text, text);
      const tokenUsage: TokenUsageMetadata = {
        model: decided.model || undefined,
        provider: 'openrouter' as any,
        responseTimeMs: Date.now() - startedAt,
        inputTokens: decided.usage?.inputTokens,
        outputTokens: decided.usage?.outputTokens,
        upstreamCostUsd: decided.usage?.costUsd,
        // ACTUAL served effort (live rollout) — receipts render this instead
        // of the display-time SSOT inference.
        ...(decided.effort ? { effort: decided.effort } : {}),
        summary: 'structured-event narration',
      };
      return { text, html: undefined, tokenUsage, success: true };
    }
    lastErr = new Error('decision transport returned empty text');
  } catch (err) {
    lastErr = err;
    console.warn(
      `[renderStructuredEvent] decision transport failed (${(err as any)?.message || err}); returning deterministic event summary`,
    );
  }

  // Decision transport failed — return a deterministic fallback. We still
  // avoid a human-language hardcoded string here; the message names the
  // structured kind so the regression is visible if it ever ships to a user.
  console.error('[renderStructuredEvent] decision transport failed:', lastErr);
  const fallback =
    `(Renderer unavailable — raw event: ${event.kind}` +
    (event.summary ? ` — ${event.summary}` : '') +
    `. Please retry in a moment.)`;
  return {
    text: fallback,
    html: undefined,
    success: false,
    errorMessage: String((lastErr as any)?.message || lastErr || 'unknown'),
  };
}
